import { makeStyles } from "@material-ui/core/styles";
import { ListSubheader, MenuItem, Select, TextField } from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { AuthContext } from "../../../../../contexts/AuthContext";
import {
  ALL_STORES,
  dashboardStores,
  isInternalUser,
  offersAllStores,
} from "../../../../../lib/dashboardAccess";
import { setStore } from "../../../../../redux/slices/store_analytics";
import { AuthorizedStores } from "../../constants";

// Below this many stores the list is short enough to scan by eye, and the
// filter box only costs a click.
const FILTER_THRESHOLD = 8;

// "All stores" is a scope rather than a store, so it stays pinned above the
// alphabetical run instead of sorting in under "A".
const byLabel = (stores) =>
  [...stores].sort((a, b) => {
    if (a.value === ALL_STORES) return -1;
    if (b.value === ALL_STORES) return 1;
    return (a.label ?? "").localeCompare(b.label ?? "", undefined, {
      sensitivity: "base",
    });
  });

/**
 * The stores this user may pick from.
 *
 * Every surface that offers a store — the picker itself, the store-management
 * page and the agent recommendations screen — reads this one hook, so they
 * cannot disagree about which stores exist. They used to call
 * `AuthorizedStores` separately, which meant a store added to the picker was
 * invisible to the code that resolves its label and client-ness.
 */
export const useStores = () => {
  const { user, method } = useContext(AuthContext);

  return useMemo(() => {
    if (!user) return [];
    // Cookie path: the stores the grants payload opens to the dashboard.
    if (method === "cookie") {
      const granted = dashboardStores(user).map((s) => ({
        value: s.code,
        label: s.display_name,
        marketplaces: s.marketplaces ?? [],
      }));
      // The grants payload has no "all" store; views that support it fan out
      // over the granted stores themselves.
      return byLabel(
        offersAllStores(user)
          ? [{ value: ALL_STORES, label: "All stores" }, ...granted]
          : granted
      );
    }
    // Legacy JWT path: derive from the hardcoded email map.
    if (!user.email) return [];
    return byLabel(AuthorizedStores(user.email));
  }, [user, method]);
};

/**
 * useStores narrowed to an atomic portfolio for internal users on the cookie
 * path, keeping "All stores". The legacy email map already lists only internal
 * stores for employees, and a granted partner keeps the stores they hold.
 * `codes` must be a stable reference, such as a module constant.
 */
export const usePortfolioStores = (codes) => {
  const { user, method } = useContext(AuthContext);
  const stores = useStores();

  return useMemo(() => {
    if (method !== "cookie" || !isInternalUser(user)) return stores;
    return stores.filter(
      (s) => s.value === ALL_STORES || codes.includes(s.value)
    );
  }, [stores, user, method, codes]);
};

export default function StorePicker({
  value,
  onStoreSelected,
  style = {},
  disabled = false,
  allowAll = false,
  options,
}) {
  const dispatch = useDispatch();
  const allStores = useStores();
  const stores = options ?? allStores;
  const [query, setQuery] = useState("");
  const selectableStores = useMemo(() => {
    if (allowAll && !stores.some((store) => store.value === ALL_STORES)) {
      return [{ value: ALL_STORES, label: "All stores" }, ...stores];
    }
    return stores;
  }, [allowAll, stores]);
  const useStyles = makeStyles({
    selectStoreStyle: {
      borderRadius: "10px",
      height: "40px",
      fontWeight: "bold",
      margin: "4px",
      "& fieldset": {
        borderWidth: "2px",
      },
    },
    customStyle: style,
    selectedMenuItem: {
      backgroundColor: "#ffffff !important",
    },
  });

  const classes = useStyles();

  const handleStoreChange = (event) => {
    const name = selectableStores.find(
      (store) => store.value === event.target.value
    )?.value;
    if (!name) return;
    onStoreSelected(name);
    dispatch(setStore(name));
  };

  const effectiveValue = selectableStores.some((store) => store.value === value)
    ? value
    : "";

  const showFilter = selectableStores.length > FILTER_THRESHOLD;

  const matchesQuery = (store) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return (
      (store.label ?? "").toLowerCase().includes(needle) ||
      store.value.toLowerCase().includes(needle)
    );
  };

  return (
    <Select
      className={`${classes.selectStoreStyle} ${classes.customStyle}`}
      labelId="store-to-select-label"
      id="store-to-select"
      value={effectiveValue}
      onChange={handleStoreChange}
      onClose={() => setQuery("")}
      disabled={disabled}
      MenuProps={{
        // The filter box owns the focus when it is there, so the menu must not
        // grab it back onto the first option.
        autoFocus: !showFilter,
        PaperProps: {
          sx: {
            maxHeight: 360,
            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: "rgba(55,111,208,0.08) !important",
            },
            "& .MuiMenuItem-root:hover": {
              backgroundColor: "rgba(0,0,0,0.04) !important",
            },
          },
        },
      }}
    >
      {showFilter && (
        <ListSubheader sx={{ px: 1, py: 1, backgroundColor: "inherit" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Filter stores"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            // Select treats printable keys as type-ahead and would steal them
            // from the input; Escape still has to reach it to close the menu.
            onKeyDown={(event) => {
              if (event.key !== "Escape") event.stopPropagation();
            }}
          />
        </ListSubheader>
      )}
      <MenuItem disabled key="select-store" value="">
        Select Store
      </MenuItem>
      {selectableStores.map((option, index) => (
        <MenuItem
          key={`${option.value} + ${index}`}
          value={option.value}
          className={classes.selectedMenuItem}
          // Filtered-out stores stay mounted so Select can still resolve the
          // current value to its label.
          sx={matchesQuery(option) ? undefined : { display: "none" }}
        >
          {option.label}
        </MenuItem>
      ))}
      {showFilter && !selectableStores.some(matchesQuery) && (
        <MenuItem disabled key="no-match" value="no-match">
          No stores match
        </MenuItem>
      )}
    </Select>
  );
}
